// imports
import { Drawer, IconButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

// local imports
import { toggleSidePanel, setPrimaryNavOpen } from '../../store/mapSlice';
import WaterDataAccordion from './WaterDataAccordion/WaterDataAccordion';
import LayersAccordion from './LayersAccordion/LayersAccordion';
import BasemapAccordion from './BasemapAccordion/BasemapAccordion';

// store
import { RootState } from '../../store';

// Arrow icon on the button to close the side panel
const BackArrow = styled(ArrowBackIosIcon)(() => ({
  height: '35px',
  width: '35px',
  marginLeft: '5px',
}));

// Arrow icon on the button to open the side panel
const ForwardArrow = styled(ArrowForwardIosIcon)(() => ({
  height: '35px',
  width: '35px',
  marginRight: '5px',
}));

// Button for opening/closing the side panel
const ToggleDrawerButton = styled(IconButton)(() => ({
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '45%',
  zIndex: 1300,
  borderRadius: '0%',
  border: '5px',
  borderTopRightRadius: '5px',
  borderBottomRightRadius: '5px',
  paddingRight: '1px',
  paddingLeft: '6px',
  marginLeft: '-2px',
  transition: 'left 0.25s',
  boxShadow: '2px 2px 2px #0003',
  '&:hover': {
    backgroundColor: '#FFFFFF',
  },
}));

// Side panel
const StyledDrawer = styled(Drawer)(({ open }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  zIndex: 1300,
  // When the drawer is closed, its root container still covers the map area
  // and blocks interactions. Disable pointer events on the root when closed,
  // and always keep them enabled on the paper so the drawer contents remain interactive.
  pointerEvents: open ? 'auto' : 'none',
  '& .MuiDrawer-paper': {
    position: 'relative',
    width: '400px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 2px 2px #0003',
    overflowY: 'auto',
    pointerEvents: 'auto',
  },

  '@media (max-width: 650px)': {
    '& .MuiDrawer-paper': {
      width: '250px',
    },
  },
}));

const SidePanel = () => {
  const dispatch = useDispatch();
  const open = useSelector((state: RootState) => state.map.sidePanelOpen);
  const primaryNavOpen = useSelector(
    (state: RootState) => state.map.primaryNavOpen
  );

  // When screen size is <491px, close primary nav when side panel opens
  useEffect(() => {
    if (open && window.innerWidth < 491 && primaryNavOpen) {
      dispatch(setPrimaryNavOpen(false));
    }
  }, [open, primaryNavOpen, dispatch]);

  // Only one accordion should be expanded at the same time
  // When an accordion is expanded, if there was another accordion expanded, it must collapse
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(
    'waterData'
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleChange =
    (panel: string) =>
    (_event: React.SyntheticEvent, newExpandedAccordion: boolean) => {
      setExpandedAccordion(newExpandedAccordion ? panel : false);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    };

  // Update state for when side panel is open/closed
  const toggleDrawer = () => {
    dispatch(toggleSidePanel());
  };

  return (
    <>
      <StyledDrawer
        variant="persistent"
        anchor="left"
        open={open}
        id="side-panel"
      >
        <div ref={scrollRef} style={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
          <WaterDataAccordion
            expanded={expandedAccordion === 'waterData'}
            onChange={handleChange('waterData')}
          />
          <LayersAccordion
            expanded={expandedAccordion === 'layers'}
            onChange={handleChange('layers')}
          />
          <BasemapAccordion
            expanded={expandedAccordion === 'basemap'}
            onChange={handleChange('basemap')}
          />
        </div>
      </StyledDrawer>
      <ToggleDrawerButton
        edge="end"
        sx={{
          left: open
            ? {
                xs: '250px',
                '@media (min-width: 650px)': {
                  left: '400px',
                },
              }
            : '0',
        }}
        size="large"
        onClick={toggleDrawer}
        title="Toggle side panel"
        aria-label="Toggle side panel"
      >
        {open ? <BackArrow /> : <ForwardArrow />}
      </ToggleDrawerButton>
    </>
  );
};

export default SidePanel;
